import { pipe, isEmpty } from 'ramda';

import { TeacherClass } from 'shared/api/onboarding';
import { ProgramGrades } from 'shared/lib/grades';

import {
  AvailableClassesMap,
  CompleteTeacherClass,
  TeacherClassWithRenamedId,
} from './types';

const renameTeacherClassSourcedId = (
  teacherClasses: TeacherClass[],
): TeacherClassWithRenamedId[] => {
  if (!teacherClasses.length) return [];

  return teacherClasses.map(({ sourcedId, grades, ...restClassProps }) => {
    return {
      classKey: sourcedId,
      grades: !grades || isEmpty(grades) ? ['Other'] : grades,
      ...restClassProps,
    };
  });
};

const trimGradeNumbers = (grades: ProgramGrades[]): string[] =>
  grades.map((grade) => grade.replace(/^0|g/i, '') as ProgramGrades);

export const getGradesLabel = ({ grades }: TeacherClassWithRenamedId) => {
  if (!grades.length || (grades as string[]).includes('Other')) return '';

  const gradeNumbers = trimGradeNumbers(grades);

  return `Grade ${gradeNumbers.join(', ')}`;
};

const sortClassesByGradesInDesc = (classes: TeacherClassWithRenamedId[]) => {
  const numericGrades = classes.filter((cl) => !cl.grades.includes('Other'));
  const otherGrades = classes.filter((cl) => cl.grades.includes('Other'));

  numericGrades.sort((a, b) => {
    const bGrade = b.grades[0];
    const aGrade = a.grades[0];
    const bValue = bGrade === 'KG' ? 0 : +bGrade;
    const aValue = aGrade === 'KG' ? 0 : +aGrade;

    if (bValue === aValue) {
      return a.displayName.localeCompare(b.displayName);
    }

    return bValue - aValue;
  });
  otherGrades.sort((a, b) => a.displayName.localeCompare(b.displayName));

  return numericGrades.concat(otherGrades);
};

const addGradesLabel = (
  classes: TeacherClassWithRenamedId[],
): CompleteTeacherClass[] =>
  classes.map((cl) => ({
    ...cl,
    gradesLabel: getGradesLabel(cl),
  }));

export const getRelevantAndSortedClasses = pipe(
  renameTeacherClassSourcedId,
  sortClassesByGradesInDesc,
  addGradesLabel,
);

export const normalizeClasses = (classes: CompleteTeacherClass[]) =>
  classes.reduce((acc, cl) => {
    acc[cl.classKey] = cl;
    return acc;
  }, {} as AvailableClassesMap);
